import React, { useMemo, useState, useEffect } from "react";
import {
  Link,
  NavLink,
  Outlet,
  useNavigate,
  useLocation
} from "react-router-dom";
import "../css/PageLayout.css";
import { NavRailIcon } from "../components/NavRailIcons.jsx";
import { isTokenAllowedForRole, decodeJwtPayload } from "../utils/jwtRole.js";
import {
  clearSessionStorage,
  isJwtExpired,
  redirectToSessionTimeout,
} from "../api/httpClient.js";
import { useRoleNavigation } from "../hooks/useRoleNavigation.js";
import { redirectToSessionTimeoutPage } from "../../app/utils/authRedirect.js";
import { getStoredAuthUser } from "../utils/authSession.js";
import { getAuthItem } from "../utils/authStorage.js";
import { formatDocumentTitle } from "../../config/siteBranding.js";
import { resolvePageTitle } from "../../config/pageTitles.js";

function initialsFromJwtPayload(payload) {
  if (!payload) return "?";
  const name = payload.name != null ? String(payload.name).trim() : "";
  if (name) {
    const parts = name.split(/\s+/).filter(Boolean);
    if (parts.length >= 2) {
      const a = parts[0][0] || "";
      const b = parts[parts.length - 1][0] || "";
      return (a + b).toUpperCase();
    }
    return name.slice(0, 2).toUpperCase();
  }
  const email = payload.email != null ? String(payload.email).trim() : "";
  if (email) return email.replace(/[^a-zA-Z0-9]/g, "").slice(0, 2).toUpperCase() || email.slice(0, 2).toUpperCase();
  return "?";
}

function IconLogout() {
  return (
    <svg className="admin-rail-icon-svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
      <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" />
      <polyline points="16 17 21 12 16 7" />
      <line x1="21" y1="12" x2="9" y2="12" />
    </svg>
  );
}

function IconMenu() {
  return (
    <svg className="admin-mobile-toggle-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" aria-hidden>
      <line x1="4" y1="7" x2="20" y2="7" />
      <line x1="4" y1="12" x2="20" y2="12" />
      <line x1="4" y1="17" x2="20" y2="17" />
    </svg>
  );
}

const PageLayout = ({
  navItems: navItemsProp,
  titleByPath: titleByPathProp = {},
  logoutPath = "/admin-login",
  requiredRoleSlug = null,
  useDynamicNav = true,
}) => {
  const navigate = useNavigate();
  const location = useLocation();

  const shouldLoadDynamicNav =
    useDynamicNav &&
    requiredRoleSlug != null &&
    String(requiredRoleSlug).trim() !== "";

  const {
    navItems: dynamicNavItems,
    titleByPath: dynamicTitleByPath,
    loading: navLoading,
    error: navError,
  } = useRoleNavigation(requiredRoleSlug, shouldLoadDynamicNav);

  const navItems = shouldLoadDynamicNav
    ? dynamicNavItems
    : Array.isArray(navItemsProp)
      ? navItemsProp
      : [];

  const titleByPath = shouldLoadDynamicNav
    ? { ...dynamicTitleByPath, ...titleByPathProp }
    : titleByPathProp;

  const [roleOk, setRoleOk] = useState(() =>
    isTokenAllowedForRole(getAuthItem("jwt_token"), requiredRoleSlug)
  );

  useEffect(() => {
    if (requiredRoleSlug == null || String(requiredRoleSlug).trim() === "") {
      setRoleOk(true);
      return;
    }
    const token = getAuthItem("jwt_token");
    if (token && isJwtExpired(token)) {
      redirectToSessionTimeout();
      return;
    }
    if (isTokenAllowedForRole(token, requiredRoleSlug)) {
      setRoleOk(true);
      return;
    }
    clearSessionStorage();
    setRoleOk(false);
    const path = redirectToSessionTimeoutPage({
      reason: "session_expired",
      from: location.pathname,
    });
    window.location.replace(path);
  }, [requiredRoleSlug, location.pathname]);

  const [openGroups, setOpenGroups] = useState({});
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const allNavItems = useMemo(() => {
    const flat = [];
    navItems.forEach((item) => {
      flat.push(item);
      if (Array.isArray(item.children)) {
        item.children.forEach((child) => flat.push(child));
      }
    });
    return flat;
  }, [navItems]);

  /** Rail profile: name, email, initials from JWT (tokens include `name` / `email` where issued). */
  const signedInProfile = useMemo(() => {
    if (typeof window === "undefined") return null;
    const storedUser = getStoredAuthUser();
    const payload = decodeJwtPayload(getAuthItem("jwt_token"));
    if (!payload && !storedUser) return null;
    const nameRaw =
      storedUser?.name != null
        ? String(storedUser.name).trim()
        : payload?.name != null
          ? String(payload.name).trim()
          : "";
    const emailRaw =
      storedUser?.email != null
        ? String(storedUser.email).trim()
        : payload?.email != null
          ? String(payload.email).trim()
          : "";
    if (!nameRaw && !emailRaw) return null;
    const displayName = nameRaw || emailRaw;
    const subtitle = nameRaw && emailRaw && emailRaw.toLowerCase() !== nameRaw.toLowerCase() ? emailRaw : "";
    const initials = initialsFromJwtPayload(payload);
    const title = subtitle ? `${displayName} · ${subtitle}` : displayName;
    return { displayName, subtitle, initials, title };
  }, [location.pathname, roleOk]);

  const pageTitle =
    titleByPath[location.pathname] ??
    allNavItems.find((i) => i.to === location.pathname)?.label ??
    resolvePageTitle(location.pathname) ??
    "Dashboard";

  useEffect(() => {
    document.title = formatDocumentTitle(pageTitle);
  }, [pageTitle]);

  useEffect(() => {
    const nextState = {};
    navItems.forEach((item) => {
      if (Array.isArray(item.children) && item.children.length > 0) {
        const key = item.to || item.label;
        const hasActiveChild = item.children.some((child) =>
          location.pathname.startsWith(child.to)
        );
        nextState[key] = hasActiveChild;
      }
    });
    setOpenGroups((prev) => ({ ...prev, ...nextState }));
  }, [location.pathname, navItems]);

  useEffect(() => {
    setMobileMenuOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    if (typeof window === "undefined") return undefined;
    const isMobile = window.matchMedia("(max-width: 768px)").matches;
    if (!isMobile) return undefined;
    const prevOverflow = document.body.style.overflow;
    if (mobileMenuOpen) {
      document.body.style.overflow = "hidden";
    }
    return () => {
      document.body.style.overflow = prevOverflow;
    };
  }, [mobileMenuOpen]);

  const handleLogout = () => {
    clearSessionStorage();
    navigate(logoutPath);
  };

  if (requiredRoleSlug != null && String(requiredRoleSlug).trim() !== "" && !roleOk) {
    return (
      <div className="admin-page admin-layout admin-layout--light">
        <div className="admin-layout-main admin-layout-main--loading">
          <p className="admin-nav-loading">Redirecting to sign in…</p>
        </div>
      </div>
    );
  }

  if (navLoading) {
    return (
      <div className="admin-page admin-layout admin-layout--light">
        <div className="admin-layout-main admin-layout-main--loading">
          <p className="admin-nav-loading">Loading menu…</p>
        </div>
      </div>
    );
  }

  return (
    <div className="admin-page admin-layout admin-layout--light">
      <div
        className={`admin-mobile-overlay${mobileMenuOpen ? " is-open" : ""}`}
        onClick={() => setMobileMenuOpen(false)}
        aria-hidden={!mobileMenuOpen}
      />
      <div className="admin-sidebar-wrap">
        <aside
          className={`admin-sidebar${mobileMenuOpen ? " is-mobile-open" : ""}`}
          aria-label="Admin navigation"
        >
          <div className="admin-sidebar-brand">
            <Link to="/" className="admin-rail-brand" title="Giri Nestham">
              <span className="admin-rail-brand-mark" aria-hidden />
              <span className="admin-rail-brand-text">Giri Nestham</span>
            </Link>
          </div>

          <nav className="admin-sidebar-nav" id="admin-sidebar-nav">
            {navError ? (
              <p className="admin-nav-error" role="alert">
                {navError}
              </p>
            ) : null}
            {navItems.map((item) => {
              const hasChildren = Array.isArray(item.children) && item.children.length > 0;
              const groupKey = item.to || item.label;
              const isParentActive =
                location.pathname === item.to ||
                (hasChildren &&
                  item.children.some((child) => location.pathname.startsWith(child.to)));

              const isGroupOpen = hasChildren ? openGroups[groupKey] ?? isParentActive : false;

              const toggleGroup = () => {
                if (!hasChildren) return;
                setOpenGroups((prev) => ({
                  ...prev,
                  [groupKey]: !isGroupOpen
                }));
              };

              return (
                <div key={groupKey}>
                  {item.to ? (
                    <NavLink
                      to={item.to}
                      end={Boolean(item.end)}
                      title={item.label}
                      className={({ isActive }) =>
                        `admin-rail-link${
                          isActive || isParentActive ? " admin-rail-link-active" : ""
                        }`
                      }
                    >
                      <span className="admin-rail-icon-wrap">
                        <NavRailIcon iconKey={item.iconKey} />
                      </span>
                      <span className="admin-rail-label">{item.label}</span>
                    </NavLink>
                  ) : (
                    <div
                      className={`admin-rail-link admin-rail-link-parent${
                        isParentActive ? " admin-rail-link-active" : ""
                      }`}
                      onClick={toggleGroup}
                      title={item.label}
                    >
                      <span className="admin-rail-icon-wrap">
                        <NavRailIcon iconKey={item.iconKey} />
                      </span>
                      <span className="admin-rail-label">{item.label}</span>
                      {hasChildren && (
                        <span
                          className={`admin-rail-group-caret${
                            isGroupOpen ? " admin-rail-group-caret-open" : ""
                          }`}
                          aria-hidden
                        />
                      )}
                    </div>
                  )}

                  {hasChildren && isGroupOpen && (
                    <div className="admin-rail-submenu">
                      {item.children.map((child) => (
                        <NavLink
                          key={child.to}
                          to={child.to}
                          end={Boolean(child.end)}
                          title={child.label}
                          className={({ isActive }) =>
                            `admin-rail-link admin-rail-link-sub${
                              isActive ? " admin-rail-link-active" : ""
                            }`
                          }
                        >
                          <span className="admin-rail-icon-wrap">
                            <NavRailIcon iconKey={child.iconKey} />
                          </span>
                          <span className="admin-rail-label">{child.label}</span>
                        </NavLink>
                      ))}
                    </div>
                  )}
                </div>
              );
            })}
          </nav>

          <div className="admin-sidebar-footer">
            {signedInProfile ? (
              <div className="admin-rail-user-card" title={signedInProfile.title}>
                <div className="admin-rail-user-avatar" aria-hidden>
                  <span className="admin-rail-user-avatar-inner">{signedInProfile.initials}</span>
                </div>
                <div className="admin-rail-user-meta">
                  <span className="admin-rail-user-display">{signedInProfile.displayName}</span>
                  {signedInProfile.subtitle ? (
                    <span className="admin-rail-user-email">{signedInProfile.subtitle}</span>
                  ) : null}
                </div>
              </div>
            ) : null}
            <button
              type="button"
              className="admin-rail-link admin-rail-link-logout"
              onClick={handleLogout}
            >
              <span className="admin-rail-icon-wrap">
                <IconLogout />
              </span>
              <span className="admin-rail-label">Logout</span>
            </button>
          </div>
        </aside>
      </div>

      <div className="admin-layout-main">
        <header className="admin-header-slim">
          <div className="admin-header-slim-row">
            <button
              type="button"
              className="admin-mobile-toggle"
              onClick={() => setMobileMenuOpen((prev) => !prev)}
              aria-label={mobileMenuOpen ? "Close navigation menu" : "Open navigation menu"}
              aria-expanded={mobileMenuOpen}
              aria-controls="admin-sidebar-nav"
            >
              <IconMenu />
            </button>
            <h1 className="admin-header-slim-title">{pageTitle}</h1>
          </div>
        </header>
        <main className="admin-main admin-layout-body">
          <Outlet />
        </main>
      </div>
    </div>
  );
};

export default PageLayout;
